import React, { useEffect } from 'react';
import classnames from 'classnames';
import { TextField } from 'new/elements/TextField';
import { HeaderField } from 'new/elements/HeaderField/HeaderField';
import { FormConfigurationStep } from 'new/components/FormConfigurationStep/FormConfigurationStep';
import {
  Checkbox,
  checkboxClasses,
  FormControlLabel,
  formControlLabelClasses,
  styled,
} from '@mui/material';
import { has } from 'lodash';
import UIUtils from 'js/lib/UIUtils';
import Button from 'js/components/elements/editors/mui/Button';
import { SUPPORT_KEY_SOURCE_FORM_WEBHOOK } from 'js/constants/supportPageKeys';
import { getCurrentSelectedOption } from '../../../elements/rselect/ReactSelectUtils';
import { CONTACT_SUPPORT_LINK } from 'js/constants/config';
import { FieldBuilderRedesigned } from 'new/elements/FieldBuilderRedesigned/FieldBuilder';

import { ConfigurationFields, IncomingWebhookAdvancedConfigurationSection } from '.';

import styles from './IncomingWebhookConfiguration.module.scss';
import { loadDataSource } from 'js/actions';
import { useDispatch } from 'react-redux';
import useAppDispatch from 'js/hooks/useAppDispatch';

const muiStyles = {
  checkboxLabel: {
    label: {
      fontSize: 14,
      fontWeight: 400,
      color: '#2F3037',
      lineHeight: '20px',
    },
  },
};

const StyledFormControlLabel = styled(FormControlLabel)({
  [`.${formControlLabelClasses.label}`]: muiStyles.checkboxLabel.label,
});

const StyledCheckbox = styled(Checkbox)({
  color: '#357BFF',
  [`& .${checkboxClasses.checked}`]: {
    color: '#357BFF',
  },
});

export function IncomingWebhookConfiguration(props: any) {
  const {
    sourceConfig,
    sourceConfigFieldsByKey,
    sourceConfigFields,
    dataSource,
    sourceBeingEdited,
    name = '',
  } = props;
  const dispatch = useAppDispatch();
  const schemaDetectionOnce =
    'schema.detection.once' in sourceConfig
      ? sourceConfig['schema.detection.once']
      : sourceConfigFieldsByKey['schema.detection.once'].default;

  const currentSource = (() => {
    if (dataSource?.id) return dataSource;
    if (sourceBeingEdited?.id) return sourceBeingEdited;
    return null;
  })();

  useEffect(() => {
    if (currentSource?.id) {
      dispatch(loadDataSource(currentSource.id));
    }
    props.setContextualSupport(SUPPORT_KEY_SOURCE_FORM_WEBHOOK);

    return () => {
      return props.unsetContextualSupport(SUPPORT_KEY_SOURCE_FORM_WEBHOOK);
    };
  }, []);

  const handleSourceConfigParamsChanged = (newConfigParams = {}) => {
    let newConfig = { ...sourceConfig, ...newConfigParams };
    newConfig = UIUtils.fillConditionalDefault(newConfig, sourceConfigFields);
    props.onSourceConfigChanged(newConfig);
  };

  const renderAuthorizationTypeSelectField = (name: string) => {
    const fieldConfig = sourceConfigFieldsByKey?.[name] ? sourceConfigFieldsByKey[name] : {};

    let currentValue = getCurrentValueField(name);

    if (name === 'webhook.auth_type') {
      // TODO: temp code to handle dropdown selection switch
      switch (currentValue) {
        case 'Payload Hash Key':
          currentValue = 'Payload Hash';
          break;
        case 'Nexla API Token':
          currentValue = 'Nexla API Key';
          break;
        default:
          break;
      }
    }

    const options = fieldConfig?.options ?? [];
    const currentSelectedOption = getCurrentSelectedOption(options, currentValue);

    return (
      <div>
        <FieldBuilderRedesigned
          isSearchable
          field={fieldConfig}
          disabled={Boolean(props?.sourceBeingEdited)}
          isRequired={fieldConfig.required}
          currentFieldValue={currentSelectedOption}
          onChange={onChange}
          fieldLabelClassName={styles.fieldLabelClassName}
        />
      </div>
    );
  };

  const checkHashEnabled = (name: string, value: string | boolean) => {
    let enabled = sourceConfig['hash.enabled'];
    if (name === 'webhook.auth_type') {
      enabled = value === 'Payload Hash Key' || value === 'Payload Hash';
    }
    return enabled;
  };

  const onChange = (name: string, value: string | boolean) => {
    handleSourceConfigParamsChanged({
      [name]: value,
      'hash.enabled': checkHashEnabled(name, value),
    });
  };

  const getCurrentValueField = (name: string) => {
    const fieldConfig = has(sourceConfigFieldsByKey, name) ? sourceConfigFieldsByKey[name] : {};

    return (() => {
      if (sourceConfig?.[name]) {
        return sourceConfig[name];
      }

      if (fieldConfig.default) {
        return fieldConfig.default;
      }

      return null;
    })();
  };

  /**
   * Update the Data Source Name
   */
  const onDataSourceNameInputOnChange = (e: any) => props.onMetaInfoChanged('name', e.target.value);

  const onCreateWebhook = () => {
    const { sourceConfig, sourceConfigFields } = props;

    let newSourceConfig = UIUtils.fillConditionalDefault(sourceConfig, sourceConfigFields);

    props.onCreateSource(newSourceConfig);
  };

  return (
    <div className={styles.container}>
      <FormConfigurationStep
        connectionName="Webhook"
        connectionIcon={
          <img
            width={16}
            height={16}
            src="https://cdn.nexla.io/ui/assets/data-sources/webhooks.svg"
            alt="incoming-icon-web-hook"
          />
        }
      >
        <div className={styles.content}>
          <section className={styles.description}>
            <span className={styles.description_text}>
              Incoming Webhooks are a simple way to post messages to Nexla. Creating an Incoming
              Webhook gives you a unique URL to which you can send a JSON/XML payload. Once Nexla
              receives data, you will find the schema in the Nexsets menu.
              <b>Webhooks enable you to:</b>
              <ul>
                <li>Push data into Nexla from your partners</li>
                <li>
                  Receive new data from partners without continuously calling an API or checking for
                  files
                </li>
                <li>Receive data instantly whenever a partner has something new to send</li>
              </ul>
            </span>
          </section>

          <section className={styles.form}>
            <div className={classnames(styles.row, styles.mb25)}>
              <div className={styles.inputWrapper} data-testid="webhook_name">
                <HeaderField item={{ display_name: 'Data Source Name*' }} />
                <TextField
                  value={name || ''}
                  placeholder="Enter a name"
                  onChange={onDataSourceNameInputOnChange}
                />
                <span className={styles.subTitle}>Required in order to generate a Webhook URL</span>
              </div>

              <HeaderField.CustomTooltipWithChildren
                toolTipBackground="dark"
                tooltipProps={{ placement: 'right' }}
                description="Enforce single Nexset detection"
              >
                <StyledFormControlLabel
                  label="Force Single Schema"
                  control={
                    <StyledCheckbox
                      disabled={props?.sourceBeingEdited}
                      checked={schemaDetectionOnce}
                      onChange={(_, isChecked) => {
                        onChange('schema.detection.once', isChecked);
                      }}
                    />
                  }
                />
              </HeaderField.CustomTooltipWithChildren>
            </div>

            <div className={styles.mb25}>
              <div>
                <HeaderField item={{ display_name: 'Authorization' }} />
              </div>
              <div>
                <span className={styles.description_text}>
                  Webhooks are authorized via Nexla API key. An additional layer of authorization
                  can be enabled via Hash-Based Message Authentication Code(HMAC). Some third
                  parties send a hash along with the payload. In such cases, Nexla can validate the
                  incoming call was generated by the authorized partner. Set HMAC properties which
                  will be used by Nexla for validation against the incoming hash. File a support
                  ticket for help:
                  <a style={{ color: '#507fe7' }} href={CONTACT_SUPPORT_LINK}>
                    support@nexla.com
                  </a>{' '}
                </span>
              </div>
            </div>

            <div className={classnames(styles.row, styles.mb25)}>
              <div className={styles.inputWrapper}>
                {renderAuthorizationTypeSelectField('webhook.auth_type')}
              </div>
            </div>

            <ConfigurationFields
              {...props}
              onChange={onChange}
              getCurrentValueField={getCurrentValueField}
            />

            {currentSource === null ? (
              <Button
                disabled={!props.name}
                data-testid="generate-webhook-url"
                buttonTheme="secondary"
                variant="outlined"
                onClick={onCreateWebhook}
              >
                <span>Generate Webhook URL</span>
              </Button>
            ) : null}

            <IncomingWebhookAdvancedConfigurationSection {...props} currentSource={currentSource} />
          </section>
        </div>
      </FormConfigurationStep>
    </div>
  );
}
